import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];
const frame = { hidden: { clipPath: "inset(12% 12% 12% 12% round 32px)" }, show: { clipPath: "inset(0% 0% 0% 0% round 28px)", transition: { duration: 1.4, ease: EASE } } };
const text = { hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE, delay: 0.2 } } };

export const PackRow = ({ pack, index }) => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const spot = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);
  const flip = index % 2 === 1;
  const onMove = (e) => {
    const r = spot.current.getBoundingClientRect();
    spot.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    spot.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.article
      ref={ref}
      data-testid={`pack-card-${pack.id}`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16"
    >
      <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        <motion.div variants={frame} ref={spot} onMouseMove={onMove} className={`sw-spot group relative overflow-hidden rounded-[28px] ${pack.full ? `mx-auto w-full ${pack.maxW}` : "aspect-[4/3]"}`} style={pack.full ? { aspectRatio: pack.ratio } : undefined}>
          {pack.full ? (
            <img data-testid={`pack-image-${pack.id}`} src={pack.image} alt={`${pack.label[lang]} — ${pack.size}`} className="absolute inset-0 h-full w-full object-contain" />
          ) : (
            <motion.img data-testid={`pack-image-${pack.id}`} style={{ y }} src={pack.image} alt={`${pack.label[lang]} — ${pack.size}`} className="absolute inset-0 h-[116%] w-full -translate-y-[7%] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]" />
          )}
          <div className="absolute left-5 top-5 rounded-full border border-[#E6D9BC]/30 bg-[#162026]/70 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#E6D9BC] backdrop-blur-md">
            {pack.size}
          </div>
        </motion.div>
      </div>
      <motion.div variants={text} className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <div className="flex items-baseline gap-4">
          <span className="font-mono text-sm text-[#E6D9BC]">{pack.no}</span>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#A3B2BC]">{pack.label[lang]}</span>
        </div>
        <h3 className="mt-4 font-display text-5xl text-[#F4EFE6] md:text-6xl">{pack.name}</h3>
        <p className="mt-5 max-w-md text-base leading-relaxed text-[#BAC6CE]">{pack.tagline[lang]}</p>
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-[#E6D9BC]">{t.pictured}</p>
        <ul className="mt-3 grid gap-x-6 sm:grid-cols-2">
          {pack.items[lang].map((it) => (
            <li key={it} className="border-b border-[#E6D9BC]/10 py-2.5 text-sm text-[#D5DDE2]">{it}</li>
          ))}
        </ul>
        <p data-testid={`pack-price-${pack.id}`} className="mt-8 font-display text-4xl text-[#F4EFE6]">{pack.price[lang]}</p>
        <p data-testid={`pack-instalment-${pack.id}`} className="mt-2 font-mono text-sm text-[#E6D9BC]">
          {t.instalmentPrefix}{new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-IE", { style: "currency", currency: "EUR" }).format(pack.amount / 4)} × 4
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a data-testid={`pack-order-link-${pack.id}`} href={pack.shop} target="_blank" rel="noopener noreferrer" className="sw-btn group">
            {t.orderBtn} <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </div>
        <p data-testid={`pack-paypal-note-${pack.id}`} className="mt-3 text-xs text-[#A3B2BC]">{t.paypalNote}</p>
      </motion.div>
    </motion.article>
  );
};
